import { ActionsPopover } from "@src/components/deprecated/ActionsPopover/ActionsPopover";
import { Icon } from "@src/components/deprecated/Icon/Icon";
import { LoadingIndicator } from "@src/components/ui-components/LoadingIndicator/LoadingIndicator";
import { GenericComponentProps, makeStyles } from "@src/utils/genericComponentProps";
import React, { CSSProperties, ReactNode, useRef } from "react";
import { AriaButtonOptions, useButton } from "react-aria";
import { ActionsPopoverAction } from "../../AdvancedSelectInput/AdvancedSelectInput";
import styles from "./Button.module.css";

export type ButtonProps = GenericComponentProps & {
  id?: string;

  /** @deprecated use `children` instead */
  title?: string;
  children?: ReactNode;

  buttonType?: "button" | "submit";
  type?: "primary" | "secondary" | "text" | "danger";

  /** @deprecated use `disabled` or `loading` props instead */
  state?: "default" | "disabled" | "loading";

  disabled?: boolean;
  loading?: boolean;
  size?: "sm" | "md";
  backgroundColor?: string;
  color?: string;
  fullWidth?: boolean;
  leadingIcon?: React.ReactElement | string;
  trailingIcon?: React.ReactElement | string;
  dropdownChevron?: boolean;
  float?: "left" | "right";
  fontSize?: number | string;
  subActions?: ActionsPopoverAction[];
  onClick?: (event: ButtonOnClickEvent) => void;
  "data-cy"?: string;
};

export type ButtonOnClickEvent = React.MouseEvent<HTMLButtonElement, MouseEvent> | React.TouchEvent<HTMLButtonElement>;

  /** @deprecated use AriaButton instead */
export const Button = ({
  id,
  type = "primary",
  children,
  title,
  state,
  buttonType = "button",
  trailingIcon,
  leadingIcon,
  dropdownChevron,
  subActions,
  onClick,
  ...props
}: ButtonProps) => {
  const buttonRef = useRef<any>(null);
  const { buttonProps } = useButton(props as AriaButtonOptions<"button">, buttonRef);
  let className = `${styles.button} ${styles[type]} ${props.className ?? ""}`;

  const isDisabled = props.disabled || state === "disabled";
  const isLoading = props.loading || state === "loading";

  if (dropdownChevron) className += ` ${styles.dropdownChevronContainer}`;
  if (props.size === "sm") className += ` ${styles.sm}`;
  if (isDisabled) className += ` ${styles.disabled}`;
  if (isLoading) className += ` ${styles.loading}`;

  const style: CSSProperties = {
    ...makeStyles(props, { width: props.fullWidth ? "100%" : undefined }),
    backgroundColor: props.backgroundColor,
    color: props.color,
    float: props.float,
    ...(props.fontSize && { fontSize: props.fontSize }),
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (onClick && !isDisabled && !isLoading) {
      onClick(event);
    }
  };
  const handleTouchEnd = (event: React.TouchEvent<HTMLButtonElement>) => {
    if (onClick && !isDisabled && !isLoading) {
      onClick(event);
    }
  };

  const leadingIconElement =
    leadingIcon &&
    (typeof leadingIcon === "string" ? (
      <Icon type="filled" cursor="pointer" name={leadingIcon} size={18} marginRight={title || children ? 10 : 0} />
    ) : (
      leadingIcon
    ));

  const trailingIconElement =
    !isLoading &&
    trailingIcon &&
    (typeof trailingIcon === "string" ? (
      <Icon type="filled" cursor="pointer" name={trailingIcon} size={18} marginLeft={title || children ? 10 : 0} />
    ) : (
      trailingIcon
    ));

  const dropdownChevronElement = (dropdownChevron || subActions) && !leadingIcon && !trailingIcon && (
    <Icon className={styles.dropdownChevron} cursor="pointer" name="keyboard_arrow_right" />
  );

  const button = (
    <button
      {...buttonProps}
      data-cy={props["data-cy"]}
      className={className}
      disabled={isDisabled}
      type={buttonType ?? "button"}
      id={id}
      onClick={handleClick}
      onTouchEnd={handleTouchEnd}
      style={style}
      ref={buttonRef}
    >
      {leadingIconElement}

      <span style={{ whiteSpace: "nowrap" }}>{children ?? title}</span>

      {isLoading && <LoadingIndicator className={styles.loadingIndicator} style={{ color: type === "primary" ? "white" : "black" }} />}

      {trailingIconElement}

      {dropdownChevronElement}
    </button>
  );

  return subActions ? <ActionsPopover actions={subActions} trigger={button} /> : button;
};
